import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Clock, History } from "lucide-react";
import dayjs from "dayjs";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { Select } from "antd";
import { OVERTIME_URL } from "../../constants/api";
import { truncateText } from "../../components/common/CommonFunctions";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { isOvertimeRequestWorkflowEnabled } from "../../utils/customConfigSession";
import { usePayrollProcessLocked } from "../../hooks/usePayrollProcessLocked";
import PayrollProcessLockNotice from "../../components/payroll/PayrollProcessLockNotice";

const formatDurationLabel = (value) => {
    if (!value) return "-";
    const parts = String(value).split(":");
    if (parts.length !== 3) return value;
    const [hours, minutes, seconds] = parts.map((part) => parseInt(part, 10) || 0);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const OvertimeChip = ({ value, tone = "neutral" }) => {
    const tones = {
        orange: "bg-orange-50 text-orange-700 border-orange-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        teal: "bg-teal-50 text-teal-700 border-teal-200",
        neutral: "bg-gray-50 text-gray-600 border-gray-200",
    };
    const label = formatDurationLabel(value);
    if (label === "-") return <span className="text-xs text-gray-400">-</span>;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${tones[tone] || tones.neutral}`}>
            {label}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const config = {
        pending: { text: "Pending", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
        approved: { text: "Approved", className: "bg-green-50 text-green-700 border-green-200" },
        rejected: { text: "Rejected", className: "bg-red-50 text-red-700 border-red-200" },
        cancelled: { text: "Cancelled", className: "bg-gray-50 text-gray-600 border-gray-200" },
    };
    const statusConfig = config[status] || { text: status || "Pending", className: "bg-blue-50 text-blue-700 border-blue-200" };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${statusConfig.className}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 shrink-0" />
            {statusConfig.text}
        </span>
    );
};

const OvertimeRequest = () => {
    const { fetch, create } = useApiMethods();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    const payrollLocked = usePayrollProcessLocked();

    const [requests, setRequests] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [eligibleRows, setEligibleRows] = useState([]);
    const [loadingEligible, setLoadingEligible] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [formState, setFormState] = useState({
        attendance_id: "",
        requested_over_time: "",
        reason: "",
    });

    useEffect(() => {
        if (!openDialog) return;

        let cancelled = false;
        const loadEligible = async () => {
            setLoadingEligible(true);
            try {
                const response = await fetchRef.current(`${OVERTIME_URL}?eligible=true`);
                if (cancelled) return;

                const rows = response?.data?.result || [];
                setEligibleRows(rows);
                if (rows.length > 0) {
                    setFormState({
                        attendance_id: String(rows[0].attendance_id),
                        requested_over_time: rows[0].requested_over_time || "",
                        reason: "",
                    });
                } else {
                    setFormState({ attendance_id: "", requested_over_time: "", reason: "" });
                }
            } catch (error) {
                if (!cancelled) {
                    ToastMsg("error", error?.message || "Failed to load eligible overtime days");
                }
            } finally {
                if (!cancelled) setLoadingEligible(false);
            }
        };

        loadEligible();
        return () => {
            cancelled = true;
        };
    }, [openDialog]);

    const handleEligibleChange = (attendanceId) => {
        const row = eligibleRows.find((item) => String(item.attendance_id) === String(attendanceId));
        setFormState((prev) => ({
            ...prev,
            attendance_id: String(attendanceId),
            requested_over_time: row?.requested_over_time || prev.requested_over_time,
        }));
    };

    const handleSubmit = async () => {
        if (!formState.attendance_id) {
            ToastMsg("error", "Select an attendance day with overtime");
            return;
        }
        if (!formState.reason || formState.reason.trim().length < 10) {
            ToastMsg("error", "Reason must be at least 10 characters");
            return;
        }

        setSubmitLoading(true);
        try {
            const response = await create(OVERTIME_URL, {
                attendance_id: formState.attendance_id,
                requested_over_time: formState.requested_over_time,
                reason: formState.reason.trim(),
                is_draft: false,
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message);
                setOpenDialog(false);
                setFormState({ attendance_id: "", requested_over_time: "", reason: "" });
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || response?.error?.[0] || "Failed to submit overtime request");
            }
        } catch (error) {
            ToastMsg("error", error?.message || "Failed to submit overtime request");
        } finally {
            setSubmitLoading(false);
        }
    };

    const openApprovalLog = useCallback((row) => {
        setSelectedRequest(row);
        setShowApprovalLog(true);
    }, []);

    const columns = useMemo(() => [
        {
            accessor: "date",
            Header: "Date",
            sortable: true,
            filterable: true,
            filterType: "date",
            width: "110px",
            Cell: ({ value }) => (
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
                    {value ? dayjs(value).format("DD-MM-YYYY") : "-"}
                </span>
            ),
        },
        {
            accessor: "detected_over_time",
            Header: "Detected OT",
            width: "120px",
            Cell: ({ value }) => <OvertimeChip value={value} tone="orange" />,
        },
        {
            accessor: "requested_over_time",
            Header: "Requested OT",
            width: "125px",
            Cell: ({ value }) => <OvertimeChip value={value} tone="amber" />,
        },
        {
            accessor: "approved_over_time",
            Header: "Approved OT",
            width: "120px",
            Cell: ({ value }) => <OvertimeChip value={value} tone="teal" />,
        },
        {
            accessor: "reason",
            Header: "Reason",
            width: "220px",
            Cell: ({ value }) => (
                value
                    ? truncateText(value, 28, "220px", "balance")
                    : <span className="text-xs text-gray-400">-</span>
            ),
        },
        {
            accessor: "approval_status",
            Header: "Status",
            filterable: true,
            filterType: "requestStatus",
            width: "120px",
            Cell: ({ value }) => <StatusBadge status={value} />,
        },
        {
            accessor: "actions",
            Header: "Action",
            align: "right",
            fixed: true,
            width: "72px",
            Cell: ({ row }) => {
                const hasLog = Array.isArray(row.approval_log) && row.approval_log.length > 0;
                return (
                    <div className="flex justify-end">
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => openApprovalLog(row)}
                            disabled={!hasLog}
                            className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${!hasLog
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                                : "bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700"
                                }`}
                            title={hasLog ? "View Approval Log" : "No approval log available"}
                        >
                            <History className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [openApprovalLog]);

    if (!isOvertimeRequestWorkflowEnabled()) {
        return <Navigate to="/home" replace />;
    }

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Clock}
                title="Overtime Request"
                subtitle="Submit overtime for days where attendance already recorded extra hours beyond your shift."
                totalRecords={totalRecords}
                buttonEnable={!payrollLocked}
                setOpenDialog={setOpenDialog}
                buttonName="New Request"
            />

            <PayrollProcessLockNotice className="mb-4" />

            <Reactable
                pagename="overtime request"
                columns={columns}
                data={requests}
                setData={setRequests}
                setTotalCount={setTotalRecords}
                serverSide
                url={OVERTIME_URL}
                serverTrigger={serverTrigger}
                rowSize="sm"
                isCSVRequired
                isPDFRequired
            />

            <DialogModal
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onSave={handleSubmit}
                title="Submit Overtime Request"
                saveLabel="Submit Request"
                closeLabel="Cancel"
                width="520px"
                loading={submitLoading || loadingEligible}
                hideSaveButton={!loadingEligible && eligibleRows.length === 0}
            >
                <div className="space-y-4">
                    {loadingEligible ? (
                        <p className="text-sm text-gray-500">Loading eligible overtime days...</p>
                    ) : eligibleRows.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            No eligible overtime days found. Overtime is detected only after checkout when worked hours exceed shift hours.
                        </p>
                    ) : (
                        <>
                            <div>
                                <label className="text-sm font-medium">Attendance Day</label>
                                <Select
                                    size="large"
                                    className="w-full mt-1"
                                    value={formState.attendance_id || undefined}
                                    onChange={handleEligibleChange}
                                    options={eligibleRows.map((row) => ({
                                        value: String(row.attendance_id),
                                        label: `${dayjs(row.date).format("DD-MM-YYYY")} • Detected ${formatDurationLabel(row.detected_over_time)}`,
                                    }))}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Requested Overtime</label>
                                <Input
                                    value={formState.requested_over_time}
                                    onChange={(e) => setFormState((prev) => ({ ...prev, requested_over_time: e.target.value }))}
                                    placeholder="HH:MM:SS"
                                />
                                <p className="text-xs text-gray-500 mt-1">Defaults to detected overtime. Cannot exceed detected hours.</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Reason</label>
                                <Textarea
                                    value={formState.reason}
                                    onChange={(e) => setFormState((prev) => ({ ...prev, reason: e.target.value }))}
                                    placeholder="Explain why overtime was required"
                                    rows={4}
                                />
                            </div>
                        </>
                    )}
                </div>
            </DialogModal>

            <DialogModal
                open={showApprovalLog}
                onClose={() => {
                    setShowApprovalLog(false);
                    setSelectedRequest(null);
                }}
                title="Overtime Approval Log"
                showActions={false}
                width="760px"
            >
                {selectedRequest && Array.isArray(selectedRequest.approval_log) && selectedRequest.approval_log.length > 0 ? (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50">
                            <span className="text-xs font-semibold text-gray-700">
                                {selectedRequest.date ? dayjs(selectedRequest.date).format("DD-MM-YYYY") : "-"}
                            </span>
                            <span className="text-gray-300">|</span>
                            <OvertimeChip value={selectedRequest.detected_over_time} tone="orange" />
                            <OvertimeChip value={selectedRequest.requested_over_time} tone="amber" />
                            {selectedRequest.approved_over_time && (
                                <OvertimeChip value={selectedRequest.approved_over_time} tone="teal" />
                            )}
                            <div className="ml-auto">
                                <StatusBadge status={selectedRequest.approval_status} />
                            </div>
                        </div>

                        <div className="overflow-x-auto max-h-60 relative rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date & Time
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Approver
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {selectedRequest.approval_log.map((log, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-800">
                                                {log.approved_date_and_time
                                                    ? dayjs(log.approved_date_and_time).format("DD-MM-YYYY hh:mm A")
                                                    : log.request_date_time
                                                        ? dayjs(log.request_date_time).format("DD-MM-YYYY hh:mm A")
                                                        : "-"}
                                            </td>
                                            <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-800">
                                                {log.approver_name || "-"}
                                            </td>
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <StatusBadge status={log.approval_status} />
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-gray-700 max-w-xs">
                                                {log.approval_notes || "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {selectedRequest.reason && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                <p className="text-xs font-semibold text-gray-600 mb-1">Request Reason</p>
                                <p className="text-xs text-gray-800">{selectedRequest.reason}</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-sm text-gray-500">No approval log available for this request.</p>
                    </div>
                )}
            </DialogModal>
        </ContainerCard>
    );
};

export default OvertimeRequest;
